import React, { useState, useEffect, useCallback, Component } from 'react';
import { botAPI } from '../services/api';

const SORT_OPTIONS = [
    { key: 'changePercent', label: 'Change %' },
    { key: 'gapPercent', label: 'Gap %' },
    { key: 'rvol', label: 'RVOL' },
    { key: 'volume', label: 'Volume' },
    { key: 'rangePercent', label: 'Range %' },
    { key: 'price', label: 'Price' },
    { key: 'vwapDiff', label: 'vs VWAP' },
];

const UNIVERSES = [
    { id: 'sp500', name: 'S&P 500', icon: '🏛️' },
    { id: 'tech50', name: 'Tech 50', icon: '💻' },
    { id: 'most_active', name: 'Most Active', icon: '⚡' },
    { id: 'meme', name: 'Meme Stocks', icon: '🦍' },
];

class ScannerErrorBoundary extends Component {
    constructor(props) { super(props); this.state = { hasError: false, error: null }; }
    static getDerivedStateFromError(error) { return { hasError: true, error }; }
    componentDidCatch(err) { console.error('[StockScanner] Render error:', err); }
    render() {
        if (this.state.hasError) {
            return (
                <div className="min-h-screen bg-bg-primary text-text-primary p-6">
                    <div className="max-w-7xl mx-auto card p-8 text-center">
                        <h1 className="text-xl font-bold text-accent-red mb-2">Scanner Error</h1>
                        <p className="text-text-muted text-sm">{this.state.error?.message || 'Failed to render scanner. Check console.'}</p>
                    </div>
                </div>
            );
        }
        return this.props.children;
    }
}

export default function StockScanner() {
    return <ScannerErrorBoundary><StockScannerInner /></ScannerErrorBoundary>;
}

function StockScannerInner() {
    const [results, setResults] = useState([]);
    const [presets, setPresets] = useState([]);
    const [loading, setLoading] = useState(false);
    const [scanInfo, setScanInfo] = useState(null);
    const [activePreset, setActivePreset] = useState(null);

    // Filters
    const [universe, setUniverse] = useState('sp500');
    const [sortBy, setSortBy] = useState('changePercent');
    const [sortDir, setSortDir] = useState('desc');
    const [filters, setFilters] = useState({
        minPrice: 5, maxPrice: '', minVolume: '', minRvol: '',
        minChange: '', maxChange: '', minGap: '', maxGap: '',
        minRange: '', gapUp: false, gapDown: false, aboveVwap: false, belowVwap: false,
    });
    const [showFilters, setShowFilters] = useState(false);
    const [customSymbols, setCustomSymbols] = useState('');

    useEffect(() => { loadPresets(); }, []);

    const loadPresets = async () => {
        try {
            const res = await botAPI.getScannerPresets();
            setPresets(res.data);
        } catch (err) { console.error('Failed to load presets'); }
    };

    const runScan = useCallback(async (overrides = {}) => {
        setLoading(true);
        try {
            const scanFilters = { ...filters, ...overrides.filters };
            // Clean empty values
            Object.keys(scanFilters).forEach(k => {
                if (scanFilters[k] === '' || scanFilters[k] === null) delete scanFilters[k];
                if (typeof scanFilters[k] === 'string' && !isNaN(scanFilters[k])) {
                    scanFilters[k] = parseFloat(scanFilters[k]);
                }
            });

            const body = {
                universe: overrides.universe || universe,
                filters: scanFilters,
                sortBy: overrides.sortBy || sortBy,
                sortDir: overrides.sortDir || sortDir,
                limit: 50,
            };

            if (body.universe === 'custom' && customSymbols) {
                body.symbols = customSymbols.split(',').map(s => s.trim()).filter(Boolean);
            }

            const res = await botAPI.runScan(body);
            setResults(res.data.results);
            setScanInfo({
                count: res.data.count,
                showing: res.data.showing,
                universe: res.data.universe,
                timestamp: res.data.timestamp,
            });
        } catch (err) {
            console.error('Scan failed:', err);
        }
        setLoading(false);
    }, [filters, universe, sortBy, sortDir, customSymbols]);

    const applyPreset = (preset) => {
        setActivePreset(preset.id);
        setFilters(f => ({
            ...f,
            ...Object.fromEntries(Object.entries(preset.filters).map(([k, v]) => [k, v]))
        }));
        setUniverse(preset.universe || 'sp500');
        setSortBy(preset.sortBy || 'changePercent');
        setSortDir(preset.sortDir || 'desc');
        // Run immediately
        runScan({
            universe: preset.universe,
            filters: preset.filters,
            sortBy: preset.sortBy,
            sortDir: preset.sortDir,
        });
    };

    const handleSort = (key) => {
        if (sortBy === key) {
            setSortDir(d => d === 'desc' ? 'asc' : 'desc');
        } else {
            setSortBy(key);
            setSortDir('desc');
        }
    };

    // Sort results client-side when sort changes
    const sorted = [...results].sort((a, b) => {
        const aV = a[sortBy] || 0;
        const bV = b[sortBy] || 0;
        return sortDir === 'desc' ? bV - aV : aV - bV;
    });

    const updateFilter = (key, val) => setFilters(f => ({ ...f, [key]: val }));

    const colorVal = (val, invert = false) => {
        if (val > 0) return invert ? 'text-red-400' : 'text-green-400';
        if (val < 0) return invert ? 'text-green-400' : 'text-red-400';
        return 'text-text-muted';
    };

    return (
        <div className="min-h-screen bg-bg-primary text-text-primary p-6">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h1 className="text-2xl font-bold">🔍 Stock Scanner</h1>
                        <p className="text-text-muted text-sm">Real-time screening with RVOL, gap %, and advanced filters</p>
                    </div>
                    <div className="flex items-center gap-2">
                        {scanInfo && (
                            <span className="text-xs text-text-muted">
                                {scanInfo.showing}/{scanInfo.count} results
                            </span>
                        )}
                        <button onClick={() => runScan()} disabled={loading}
                            className="px-4 py-2 bg-accent-purple text-white rounded-lg text-sm font-medium disabled:opacity-50">
                            {loading ? '⏳ Scanning...' : '🔍 Scan'}
                        </button>
                    </div>
                </div>

                {/* Presets */}
                <div className="flex gap-2 mb-4 flex-wrap">
                    {presets.map(p => (
                        <button key={p.id} onClick={() => applyPreset(p)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                                activePreset === p.id
                                    ? 'bg-accent-purple text-white'
                                    : 'bg-bg-card border border-border-color text-text-secondary hover:bg-bg-tertiary'
                            }`}>
                            {p.name}
                        </button>
                    ))}
                </div>

                {/* Universe + Filters Bar */}
                <div className="card p-3 mb-4">
                    <div className="flex items-center gap-3 flex-wrap">
                        <span className="text-xs font-bold text-text-muted uppercase">Universe:</span>
                        {UNIVERSES.map(u => (
                            <button key={u.id} onClick={() => setUniverse(u.id)}
                                className={`px-2 py-1 rounded text-xs ${universe === u.id ? 'bg-accent-purple/20 text-accent-purple font-bold' : 'text-text-muted hover:text-text-secondary'}`}>
                                {u.icon} {u.name}
                            </button>
                        ))}
                        <button onClick={() => setUniverse('custom')}
                            className={`px-2 py-1 rounded text-xs ${universe === 'custom' ? 'bg-accent-purple/20 text-accent-purple font-bold' : 'text-text-muted hover:text-text-secondary'}`}>
                            ✏️ Custom
                        </button>

                        <div className="ml-auto">
                            <button onClick={() => setShowFilters(!showFilters)}
                                className="px-3 py-1 rounded text-xs bg-bg-tertiary text-text-muted hover:text-text-secondary">
                                {showFilters ? '▲ Hide Filters' : '▼ Filters'}
                            </button>
                        </div>
                    </div>

                    {universe === 'custom' && (
                        <div className="mt-2">
                            <input type="text" value={customSymbols}
                                onChange={e => setCustomSymbols(e.target.value.toUpperCase())}
                                placeholder="AAPL, MSFT, NVDA, TSLA..."
                                className="w-full bg-bg-tertiary border border-border-color rounded px-3 py-1.5 text-sm" />
                        </div>
                    )}

                    {showFilters && (
                        <div className="mt-3 pt-3 border-t border-border-color/30">
                            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                                <FilterInput label="Min Price" value={filters.minPrice} onChange={v => updateFilter('minPrice', v)} />
                                <FilterInput label="Max Price" value={filters.maxPrice} onChange={v => updateFilter('maxPrice', v)} />
                                <FilterInput label="Min Volume" value={filters.minVolume} onChange={v => updateFilter('minVolume', v)} placeholder="e.g. 1000000" />
                                <FilterInput label="Min RVOL" value={filters.minRvol} onChange={v => updateFilter('minRvol', v)} placeholder="e.g. 1.5" />
                                <FilterInput label="Min Change %" value={filters.minChange} onChange={v => updateFilter('minChange', v)} />
                                <FilterInput label="Max Change %" value={filters.maxChange} onChange={v => updateFilter('maxChange', v)} />
                                <FilterInput label="Min Gap %" value={filters.minGap} onChange={v => updateFilter('minGap', v)} />
                                <FilterInput label="Max Gap %" value={filters.maxGap} onChange={v => updateFilter('maxGap', v)} />
                                <FilterInput label="Min Range %" value={filters.minRange} onChange={v => updateFilter('minRange', v)} />
                            </div>
                            <div className="flex gap-4 mt-2">
                                <ToggleFilter label="Gap Up Only" active={filters.gapUp} onClick={() => updateFilter('gapUp', !filters.gapUp)} />
                                <ToggleFilter label="Gap Down Only" active={filters.gapDown} onClick={() => updateFilter('gapDown', !filters.gapDown)} />
                                <ToggleFilter label="Above VWAP" active={filters.aboveVwap} onClick={() => updateFilter('aboveVwap', !filters.aboveVwap)} />
                                <ToggleFilter label="Below VWAP" active={filters.belowVwap} onClick={() => updateFilter('belowVwap', !filters.belowVwap)} />
                            </div>
                        </div>
                    )}
                </div>

                {/* Results Table */}
                {results.length === 0 && !loading ? (
                    <div className="card p-12 text-center text-text-muted">
                        <div className="text-5xl mb-4">🔍</div>
                        <p className="text-lg font-medium mb-2">Select a preset or configure filters</p>
                        <p className="text-sm">Click a preset above or hit Scan to find stocks matching your criteria.</p>
                    </div>
                ) : (
                    <div className="card overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="bg-bg-tertiary text-text-muted text-xs uppercase tracking-wider">
                                        <th className="px-3 py-2 text-left">Symbol</th>
                                        <SortHeader label="Price" field="price" current={sortBy} dir={sortDir} onClick={handleSort} />
                                        <SortHeader label="Change %" field="changePercent" current={sortBy} dir={sortDir} onClick={handleSort} />
                                        <SortHeader label="Gap %" field="gapPercent" current={sortBy} dir={sortDir} onClick={handleSort} />
                                        <SortHeader label="RVOL" field="rvol" current={sortBy} dir={sortDir} onClick={handleSort} />
                                        <SortHeader label="Volume" field="volume" current={sortBy} dir={sortDir} onClick={handleSort} />
                                        <SortHeader label="Range %" field="rangePercent" current={sortBy} dir={sortDir} onClick={handleSort} />
                                        <SortHeader label="vs VWAP" field="vwapDiff" current={sortBy} dir={sortDir} onClick={handleSort} />
                                        <th className="px-3 py-2 text-right">O/H/L</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sorted.map((r, i) => (
                                        <tr key={r.symbol} className={`border-t border-border-color/20 hover:bg-bg-tertiary/50 ${i % 2 === 0 ? '' : 'bg-bg-card/30'}`}>
                                            <td className="px-3 py-2 font-bold">{r.symbol}</td>
                                            <td className="px-3 py-2 text-right font-mono">${r.price}</td>
                                            <td className={`px-3 py-2 text-right font-mono font-bold ${colorVal(r.changePercent)}`}>
                                                {r.changePercent > 0 ? '+' : ''}{r.changePercent}%
                                            </td>
                                            <td className={`px-3 py-2 text-right font-mono ${colorVal(r.gapPercent)}`}>
                                                {r.gapPercent > 0 ? '+' : ''}{r.gapPercent}%
                                            </td>
                                            <td className={`px-3 py-2 text-right font-mono ${r.rvol >= 2 ? 'text-yellow-400 font-bold' : r.rvol >= 1.5 ? 'text-yellow-400' : 'text-text-muted'}`}>
                                                {r.rvol}x
                                            </td>
                                            <td className="px-3 py-2 text-right font-mono text-text-muted">{r.volumeFormatted}</td>
                                            <td className={`px-3 py-2 text-right font-mono ${r.rangePercent >= 3 ? 'text-yellow-400' : 'text-text-muted'}`}>
                                                {r.rangePercent}%
                                            </td>
                                            <td className={`px-3 py-2 text-right font-mono ${colorVal(r.vwapDiff)}`}>
                                                {r.vwapDiff > 0 ? '+' : ''}{r.vwapDiff}%
                                            </td>
                                            <td className="px-3 py-2 text-right font-mono text-xs text-text-muted">
                                                {r.open}/{r.high}/{r.low}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        {scanInfo && (
                            <div className="px-3 py-2 bg-bg-tertiary text-xs text-text-muted flex justify-between">
                                <span>Scanned: {scanInfo.universe.toUpperCase()} • {scanInfo.showing} of {scanInfo.count} matches</span>
                                <span>{new Date(scanInfo.timestamp).toLocaleTimeString()}</span>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

function FilterInput({ label, value, onChange, placeholder }) {
    return (
        <div>
            <label className="block text-xs text-text-muted mb-0.5">{label}</label>
            <input type="number" step="any" value={value}
                onChange={e => onChange(e.target.value)}
                placeholder={placeholder || ''}
                className="w-full bg-bg-tertiary border border-border-color/50 rounded px-2 py-1 text-xs" />
        </div>
    );
}

function ToggleFilter({ label, active, onClick }) {
    return (
        <button onClick={onClick}
            className={`text-xs px-2 py-1 rounded ${active ? 'bg-accent-purple/20 text-accent-purple font-bold' : 'text-text-muted hover:text-text-secondary'}`}>
            {active ? '✓ ' : ''}{label}
        </button>
    );
}

function SortHeader({ label, field, current, dir, onClick }) {
    const isActive = current === field;
    return (
        <th className="px-3 py-2 text-right cursor-pointer select-none hover:text-text-primary"
            onClick={() => onClick(field)}>
            {label} {isActive ? (dir === 'desc' ? '▼' : '▲') : ''}
        </th>
    );
}
